import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api, getUTM, trackLead, errMsg } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Field, SelectField, Pills, CityTags, DateField } from "./fields";
import { validateContact, budgetTooLow } from "./validators";
import { StepBar, ContactStep, FormNav } from "./OwnerForm";
import ThankYou from "./ThankYou";
import { toast } from "sonner";

const EMPTY = {
  cities: [], property_type: "", min_rooms: "", budget_min: 1000, budget_max: 3000,
  move_date: "", stay_duration: "",
  occupants: "", work_status: "", income_range: "", pets: "", smoking: "", inschrijving_needed: "",
  name: "", phone: "+31 ", email: "", preferred_channel: "", consent: false, website: "",
};

export default function TenantForm({ config }) {
  const { t, lang } = useLang();
  const [step, setStep] = useState(1);
  const [v, setV] = useState(() => {
    try {
      const d = localStorage.getItem("he_draft_tenant");
      return d ? { ...EMPTY, ...JSON.parse(d) } : EMPTY;
    } catch {
      return EMPTY;
    }
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (patch) => setV((p) => ({ ...p, ...patch }));

  useEffect(() => {
    localStorage.setItem("he_draft_tenant", JSON.stringify(v));
  }, [v]);

  const showBudgetWarning = budgetTooLow(v.cities, v.budget_max);

  const validateStep1 = () => {
    const e = {};
    if (!v.cities.length) e.cities = t.form.errors.required;
    if (!v.property_type) e.property_type = t.form.errors.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (step === 1 && !validateStep1()) return;
    setStep((s) => Math.min(3, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    const e = validateContact(v, t);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setSubmitting(true);
    try {
      await api.post("/leads/tenant", {
        cities: v.cities, property_type: v.property_type, min_rooms: v.min_rooms,
        budget_min: v.budget_min, budget_max: v.budget_max, move_date: v.move_date,
        stay_duration: v.stay_duration, occupants: v.occupants, work_status: v.work_status,
        income_range: v.income_range, pets: v.pets, smoking: v.smoking,
        inschrijving_needed: v.inschrijving_needed,
        name: v.name, phone: v.phone, email: v.email, preferred_channel: v.preferred_channel,
        consent: v.consent, meta: getUTM(), lang, website: "",
      });
      trackLead("tenant");
      localStorage.removeItem("he_draft_tenant");
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return <ThankYou type="tenant" config={config} />;

  return (
    <div data-testid="tenant-form">
      <StepBar step={step} />
      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.3 }} className="space-y-5">
          {step === 1 && (
            <>
              <h4 className="text-sm font-semibold uppercase tracking-[0.15em] text-stone">{t.form.steps.look}</h4>
              <Field label={t.form.labels.cities} error={errors.cities}>
                <CityTags value={v.cities} onChange={(x) => set({ cities: x })} placeholder={t.form.labels.citiesPlaceholder} error={errors.cities} testId="tenant-cities" />
              </Field>
              <Field label={t.form.labels.propertyType} error={errors.property_type}>
                <Pills value={v.property_type} onChange={(x) => set({ property_type: x })} options={t.form.options.propertyTypeTenant} testId="tenant-type" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label={t.form.labels.minRooms}>
                  <SelectField value={v.min_rooms} onChange={(x) => set({ min_rooms: x })} options={["1", "2", "3", "4", "5+"]} placeholder="—" testId="tenant-minrooms" />
                </Field>
                <Field label={t.form.labels.stayDuration}>
                  <SelectField value={v.stay_duration} onChange={(x) => set({ stay_duration: x })} options={t.form.options.stay} placeholder="—" testId="tenant-stay" />
                </Field>
              </div>
              <Field label={`${t.form.labels.budget}: €${v.budget_min.toLocaleString("nl-NL")} – €${v.budget_max.toLocaleString("nl-NL")}${v.budget_max >= 10000 ? "+" : ""}`}>
                <div className="pt-3 px-1">
                  <Slider
                    value={[v.budget_min, v.budget_max]}
                    min={1000}
                    max={10000}
                    step={250}
                    onValueChange={([mn, mx]) => set({ budget_min: mn, budget_max: mx })}
                    data-testid="tenant-budget"
                  />
                </div>
              </Field>
              {showBudgetWarning && (
                <div className="flex items-start gap-2 rounded-md bg-mist border border-line p-3 text-sm text-stone" data-testid="tenant-budget-warning">
                  <Info size={16} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                  <span>{t.form.budgetWarning}</span>
                </div>
              )}
              <Field label={t.form.labels.moveDate}>
                <DateField value={v.move_date} onChange={(x) => set({ move_date: x })} testId="tenant-move-date" futureOnly />
              </Field>
            </>
          )}

          {step === 2 && (
            <>
              <h4 className="text-sm font-semibold uppercase tracking-[0.15em] text-stone">{t.form.steps.situation}</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label={t.form.labels.occupants}>
                  <SelectField value={v.occupants} onChange={(x) => set({ occupants: x })} options={["1", "2", "3", "4", "5+"]} placeholder="—" testId="tenant-occupants" />
                </Field>
                <Field label={t.form.labels.workStatus}>
                  <SelectField value={v.work_status} onChange={(x) => set({ work_status: x })} options={t.form.options.work} placeholder="—" testId="tenant-work" />
                </Field>
              </div>
              <Field label={t.form.labels.income}>
                <SelectField value={v.income_range} onChange={(x) => set({ income_range: x })} options={t.form.options.income} placeholder="—" testId="tenant-income" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t.form.labels.pets}>
                  <Pills value={v.pets} onChange={(x) => set({ pets: x })} options={t.form.options.pets} testId="tenant-pets" />
                </Field>
                <Field label={t.form.labels.smoking}>
                  <Pills value={v.smoking} onChange={(x) => set({ smoking: x })} options={t.form.options.yesNo} testId="tenant-smoking" />
                </Field>
              </div>
            </>
          )}

          {step === 3 && <ContactStep v={v} set={set} errors={errors} t={t} testPrefix="tenant" disclosure={t.form.tenantDisclosure} />}
        </motion.div>
      </AnimatePresence>

      <FormNav step={step} onNext={next} onBack={back} onSubmit={submit} submitting={submitting} t={t} testPrefix="tenant" />
    </div>
  );
}
